import { useCallback, useEffect, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';

import { type MentionProvider } from '@atlaskit/mention/resource';
import type { MentionAgentSource } from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import { SmartMentionResource } from '@atlassian/smart-mention-resource';

import {
	agentMentionAnalyticsCallbacks,
	type AgentMentionAnalyticsEvent,
} from '../services/rovo-chat-mention-resource/agent-mention-analytics-event';
import { createRovoChatMentionResource } from '../services/rovo-chat-mention-resource/create-rovo-chat-mention-resource';
import {
	RovoChatMentionResource,
	type SelectedAgentRef,
} from '../services/rovo-chat-mention-resource/rovo-chat-mention-resource';

/** Active users only — no Connect bots, no apps. Agents come from the
 *  Relay-backed `MentionAgentSource`, not URS. */
const ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER =
	'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app)';

type UseChatMentionResourceOptions = {
	productKey: string;
	siteId: string;
	orgId: string | undefined;
	currentUserId: string | undefined;
	/**
	 * Optional consumer-provided mention provider. Three cases are
	 * recognised in v3:
	 *
	 *   1. `SmartMentionResource` instance → wrapped in a fresh
	 *      `RovoChatMentionResource` so the host's people search keeps
	 *      working AND Rovo agents are folded in. The host retains
	 *      ownership of the SMR lifecycle (we do NOT mark it as owned).
	 *   2. Any other `MentionProvider` (test harness, embed surface
	 *      with custom logic) → passed through unchanged; the picker is
	 *      people-only because we don't have a SMR to wrap.
	 *   3. `undefined` → we build URS internally via
	 *      `createRovoChatMentionResource`, which takes ownership.
	 */
	mentionResourceOverride: MentionProvider | undefined;
	/**
	 * When `true`, the resource pulls agent rows from `agentSource` and
	 * folds them under people in the typeahead. Off by default.
	 */
	enableAgents: boolean;
	/**
	 * The agent source from `useMentionAgentSource()`. `null` /
	 * `undefined` is tolerated and produces a people-only picker.
	 * Required only when `enableAgents` is `true`; safe to omit when
	 * agents are disabled (e.g. inline-chat-prompt surfaces).
	 */
	agentSource?: MentionAgentSource | null | undefined;
	/**
	 * Fired when the user picks an agent row. Function-form so the
	 * handler can be swapped without rebuilding the resource.
	 * Required only when `enableAgents` is `true`.
	 */
	onAgentSelected?: (agent: SelectedAgentRef) => void;
	/**
	 * Getter for the conversation's currently-active agent ids. Read
	 * lazily so the predicate sees the freshest store state without
	 * rebuilding the resource on every selection change. When omitted,
	 * chips are never disabled.
	 */
	getSelectedAgentIds?: () => readonly string[];
	/**
	 * Translated tooltip rendered on disabled (non-active) agent chips.
	 * Omit to make the disabled-state predicate a no-op (graceful
	 * degradation for surfaces that haven't wired translations).
	 */
	disabledAgentTooltip?: string;
	/**
	 * Fires when an agent-mention chip is destroyed in the editor.
	 * Forwarded only for ids in `getSelectedAgentIds()`.
	 */
	onAgentMentionDestroyed?: (agentId: string) => void;
	onAgentMentionAnalyticsEvent?: (event: AgentMentionAnalyticsEvent) => void;
	/**
	 * True when the conversation is a dedicated (non-default) agent chat
	 */
	isDedicatedAgentChat?: boolean;
	/**
	 * i18n label for the synthetic "Rovo" mention row. Resolved via `formatMessage` by the
	 * caller since the resource is a plain class. The row only appears when this is provided.
	 */
	rovoMentionLabel?: string;
};

/** Owns the lifecycle of the @-mention provider for Rovo Chat. Disposes
 *  the `RovoChatMentionResource` on replacement / unmount; bare
 *  `MentionProvider` overrides are passed through untouched. */
export const useChatMentionResource = ({
	productKey,
	siteId,
	orgId,
	currentUserId,
	mentionResourceOverride,
	enableAgents,
	agentSource,
	onAgentSelected,
	getSelectedAgentIds,
	disabledAgentTooltip,
	onAgentMentionDestroyed,
	onAgentMentionAnalyticsEvent,
	isDedicatedAgentChat,
	rovoMentionLabel,
}: UseChatMentionResourceOptions): Promise<MentionProvider> => {
	di(SmartMentionResource);

	const onAgentSelectedRef = useRef(onAgentSelected);
	onAgentSelectedRef.current = onAgentSelected;
	const getSelectedAgentIdsRef = useRef(getSelectedAgentIds);
	getSelectedAgentIdsRef.current = getSelectedAgentIds;
	const onAgentMentionDestroyedRef = useRef(onAgentMentionDestroyed);
	onAgentMentionDestroyedRef.current = onAgentMentionDestroyed;
	const disabledAgentTooltipRef = useRef(disabledAgentTooltip);
	disabledAgentTooltipRef.current = disabledAgentTooltip;
	const onAgentMentionAnalyticsEventRef = useRef(onAgentMentionAnalyticsEvent);
	onAgentMentionAnalyticsEventRef.current = onAgentMentionAnalyticsEvent;

	const handleAgentSelected = useCallback((agent: SelectedAgentRef) => {
		onAgentSelectedRef.current?.(agent);
	}, []);
	const getCurrentSelectedAgentIds = useCallback(
		() => getSelectedAgentIdsRef.current?.() ?? [],
		[],
	);
	const handleAgentMentionDestroyed = useCallback((agentId: string) => {
		onAgentMentionDestroyedRef.current?.(agentId);
	}, []);
	const handleAgentMentionAnalyticsEvent = useCallback((event: AgentMentionAnalyticsEvent) => {
		onAgentMentionAnalyticsEventRef.current?.(event);
	}, []);

	const provider = useMemo<MentionProvider>(() => {
		const sharedAgentOptions = {
			agentSource: enableAgents ? (agentSource ?? undefined) : undefined,
			onAgentSelected: handleAgentSelected,
			getSelectedAgentIds: getCurrentSelectedAgentIds,
			disabledAgentTooltip: disabledAgentTooltipRef.current,
			onAgentMentionDestroyed: handleAgentMentionDestroyed,
			isDedicatedAgentChat: enableAgents ? isDedicatedAgentChat : false,
			rovoMentionLabel: enableAgents ? rovoMentionLabel : undefined,
		};

		// CASE 1: host handed us a SmartMentionResource → wrap it so we
		// add agent support without taking ownership of the SMR
		// lifecycle (the host's singleton stays alive after we dispose).
		if (mentionResourceOverride instanceof SmartMentionResource) {
			return new RovoChatMentionResource({
				peopleResource: mentionResourceOverride,
				...sharedAgentOptions,
			});
		}

		// CASE 2: any other provider override → pass through unchanged.
		// People-only because we cannot wrap a non-SMR with agent
		// support (the agent-merge layer needs SMR's subscribe API).
		if (mentionResourceOverride) {
			return mentionResourceOverride;
		}

		// CASE 3: default — build URS internally; factory marks it as
		// owned so dispose() will tear it down.
		return createRovoChatMentionResource({
			peopleConfig: {
				productKey,
				siteId,
				includeTeams: true,
				includeUsers: true,
				orgId,
				searchQueryFilter: ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER,
				productAttributes: { client: 'rovo-chat' },
				shouldHighlightMention: (mention) => mention.id === currentUserId,
			},
			...sharedAgentOptions,
		});
	}, [
		productKey,
		siteId,
		orgId,
		currentUserId,
		mentionResourceOverride,
		enableAgents,
		agentSource,
		handleAgentSelected,
		getCurrentSelectedAgentIds,
		handleAgentMentionDestroyed,
		isDedicatedAgentChat,
		rovoMentionLabel,
	]);

	useEffect(() => {
		if (!(provider instanceof RovoChatMentionResource)) {
			return;
		}

		agentMentionAnalyticsCallbacks.set(
			provider,
			enableAgents ? handleAgentMentionAnalyticsEvent : undefined,
		);
		return () => {
			agentMentionAnalyticsCallbacks.set(provider, undefined);
		};
	}, [provider, enableAgents, handleAgentMentionAnalyticsEvent]);

	useEffect(() => {
		if (provider instanceof RovoChatMentionResource) {
			provider.setDisabledAgentTooltip(disabledAgentTooltip);
		}
	}, [disabledAgentTooltip, provider]);
	useEffect(() => {
		if (!(provider instanceof RovoChatMentionResource)) {
			return;
		}
		return () => provider.dispose();
	}, [provider]);

	return useMemo(() => Promise.resolve(provider), [provider]);
};
